import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowLeft, Printer } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { RunReportVersionParams } from '@/backend/access/runReport/types';
import { ReportEvaluation, ReportValues, RunReportContext, asRunReport } from '@/reports/types';
import { useRunReportStore } from '@/frontend/store/RunReportStore';
import { ReportAssetsProvider, ReportEditingProvider } from '@/frontend/reports/primitives';
import { useReportModule } from '@/frontend/reports/useReportModule';
import { ReportViewProps } from '@/frontend/reports/types';
import { LoadingSpinner } from '../LoadingSpinner';
import { VerdictBadge } from './ReportBadges';

/**
 * A published test report, as its own page: the frozen values and the run as
 * it was read, rendered read-only and printable — with the bundle version it
 * was published against when it is a bundled report. Images still come from
 * the run, so a file removed from the run since shows as unavailable.
 */
export function RunReportVersionPage() {
  const { runId = '', versionId = '' } = useParams();
  const fetchState = useRunReportStore((s) => s.fetchState);
  const fetchVersion = useRunReportStore((s) => s.fetchVersion);
  const fileUrls = useRunReportStore((s) => s.fileUrls);
  const [version, setVersion] = useState<RunReportVersionParams | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      await fetchState(runId);
      setVersion(await fetchVersion(runId, versionId));
    })().finally(() => setLoading(false));
  }, [runId, versionId, fetchState, fetchVersion]);

  const { module, loading: moduleLoading, error: moduleError } = useReportModule(version ? { key: version.reportKey, bundleVersionId: version.reportBundleVersionId ?? null } : null);
  const definition = module?.definition ? asRunReport(module.definition) : null;
  const components = module?.components;
  const values = useMemo(() => (version && definition ? definition.normalizeValues(version.values) : null), [version, definition]);
  const model = useMemo(() => (definition && values && version ? definition.evaluate(values, version.context) : null), [definition, values, version]);
  const resolveFiles = useCallback((ids: string[]) => fileUrls(runId, ids), [fileUrls, runId]);
  const back = `/runs/${runId}/report`;

  if (loading || moduleLoading) {
    return <div className="d-flex justify-content-center py-5" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
  }
  if (!version || !definition || !components || !values || !model) {
    return (
      <div className="p-4">
        <p className="text-muted">{moduleError ? I18n.get('reportBundleLoadFailed', { message: moduleError }) : I18n.get('modelNotFound', { model: I18n.get('runReportVersion') })}</p>
        <Link to={back}>{I18n.get('backToTestReport')}</Link>
      </div>
    );
  }

  const Document = components.Document as unknown as ComponentType<ReportViewProps<ReportValues, ReportEvaluation, RunReportContext>>;
  return (
    <div className="overflow-auto" style={{ maxHeight: '100vh', height: '100vh' }}>
      <div className="d-print-none d-flex justify-content-between align-items-center gap-3 px-4 pt-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Link to={back} className="btn btn-blue-light fusa-btn-small d-flex align-items-center gap-1"><ArrowLeft size={14} /> {I18n.get('backToTestReport')}</Link>
          <span className="fw-bold">{definition.title}</span>
          <span className="text-muted small">{I18n.get('version')} {version.number} · {new Date(version.createdAt ?? '').toLocaleString()} · {version.publishedBy}</span>
          <VerdictBadge verdict={version.verdict} label={version.verdictLabel} />
          {version.reportVersion !== definition.version && <span className="badge bg-light text-dark" title={I18n.get('definitionChangedSinceHelp')}>{I18n.get('definitionChangedSince', { then: version.reportVersion, now: definition.version })}</span>}
        </div>
        <Button className="btn-blue-light fusa-btn-small d-flex align-items-center gap-1" onClick={() => window.print()}><Printer size={14} /> {I18n.get('print')}</Button>
      </div>
      <ReportAssetsProvider resolve={resolveFiles}>
        <ReportEditingProvider readOnly>
          <Document
            values={values}
            model={model}
            context={version.context}
            meta={{ reportVersion: version.reportVersion, versionNumber: version.number, publishedAt: typeof version.createdAt === 'string' ? version.createdAt : version.createdAt?.toISOString(), publishedBy: version.publishedBy }}
          />
        </ReportEditingProvider>
      </ReportAssetsProvider>
    </div>
  );
}
